import { FormattedMessage } from 'react-intl';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import TimestampAndMileage from '@/features/engineoil/components/TimestampAndMileage';
import { getBarColorClass } from '@/features/engineoil/utils/utils';
import type { EngineOilRow } from './types';

const OutdatedExclamationSign = () => (
    <OverlayTrigger
        placement="top"
        overlay={
            <Tooltip>
                <FormattedMessage id="intl-msg:engineOil.olderThan24h" />
            </Tooltip>
        }
    >
        <span
            className={
                'uikit-icon icon-exclamation-sign text-color-dark ' +
                'text-size-16 width-auto margin-top-0 margin-left-5'
            }
        />
    </OverlayTrigger>
);

interface DtcDangerSignProps {
    activeDtc: NonNullable<EngineOilRow['activeDtc']>;
}

const DtcDangerSign = ({ activeDtc }: DtcDangerSignProps) => (
    <OverlayTrigger
        placement="top"
        overlay={
            <Tooltip>
                <FormattedMessage id={`intl-msg:engineOil.card.workshop.dtc.${activeDtc.type}`} />
                &nbsp;(
                <FormattedMessage id="intl-msg:engineOil.dtc.spn" />
                &nbsp;
                {activeDtc.spn})
            </Tooltip>
        }
    >
        <span
            className={
                'uikit-icon icon-error-sign text-color-danger ' + 'text-size-16 width-auto margin-top-0 margin-left-5'
            }
        />
    </OverlayTrigger>
);

interface EngineOilValueCellProps {
    row: EngineOilRow;
}

const EngineOilValueCell = ({ row }: EngineOilValueCellProps) => {
    const { status, measurement, isReadingNotPossible, timestamp, mileage, activeDtc } = row;

    if (measurement === null) {
        return (
            <div className="text-color-dark">
                <FormattedMessage id="intl-msg:engineOil.noDataAvailable" />
            </div>
        );
    }

    // An active DTC is always critical and must be shown, even when the reading is outdated.
    const showDtc = activeDtc != null;
    const barColorClass = getBarColorClass(status, isReadingNotPossible, showDtc);
    const textColorClass = isReadingNotPossible ? 'text-color-gray' : 'text-color-gray-darkest';
    const percentage = Math.round(measurement);

    return (
        <div>
            <div className="display-flex align-items-center">
                <div className="width-100pct display-flex align-items-center">
                    <div className="progress margin-0 flex-1-0">
                        <div className={`progress-bar ${barColorClass}`.trim()} style={{ width: `${percentage}%` }} />
                    </div>
                    <div className={`margin-left-10 ${textColorClass}`}>{`${percentage} %`}</div>
                </div>
                {isReadingNotPossible && <OutdatedExclamationSign />}
                {showDtc && <DtcDangerSign activeDtc={activeDtc} />}
            </div>

            <div className="display-flex flex-wrap margin-top-5">
                {isReadingNotPossible && (
                    <div className="text-size-14 text-color-dark">
                        <FormattedMessage id="intl-msg:engineOil.latestValidReading" />
                        &nbsp;
                    </div>
                )}
                <TimestampAndMileage timestamp={timestamp} mileage={mileage} />
            </div>

            {isReadingNotPossible && (
                <div className="text-size-14 margin-top-10">
                    <FormattedMessage id="intl-msg:engineOil.state.reading_not_possible" />
                </div>
            )}
        </div>
    );
};

export default EngineOilValueCell;
